import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { DebateCard } from './debate-card'
import { useAxiosQuery } from '@/hooks/useAxiosQuery'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import Loader from '@/components/loaders/Loader'
import { Paginator } from '@/components/ui/paginator'
import { useSocket } from '@/contexts/SocketProvider'
import { Button } from '@/components/ui/button'
import { ChevronDown, ChevronUp } from 'lucide-react'

export const DebateGrid = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFormat, setSelectedFormat] = useState('all')
  const [subjectFilter, setSubjectFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [debates, setDebates] = useState([])
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedFormat, subjectFilter])

  const queryParams = {
    ...(searchTerm.trim() !== '' && { search: searchTerm }),
    ...(selectedFormat !== 'all' && { format: selectedFormat }),
    ...(subjectFilter !== 'all' && { subject: subjectFilter }),
  }

  const { data, isFetching } = useAxiosQuery({
    url: '/api/halls',
    params: queryParams,
  })

  useEffect(() => {
    if (data) setDebates(data)
  }, [data])

  useSocket({
    'new-hall': (newHall) => {
      const matchesFilters =
        newHall.isPublic &&
        (selectedFormat === 'all' || newHall.format === selectedFormat) &&
        (subjectFilter === 'all' || newHall.subject === subjectFilter) &&
        (searchTerm === '' ||
          newHall.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          newHall.topic.toLowerCase().includes(searchTerm.toLowerCase()))

      if (matchesFilters) {
        setDebates((prev) =>
          prev.some((d) => d.hallId === newHall.hallId) ? prev : [newHall, ...prev],
        )
      }
    },
    'hall-deleted': (deletedHallId) => {
      setDebates((prev) => prev.filter((debate) => debate.id !== deletedHallId))
    },
  })

  const debatesPerPage = isExpanded ? 12 : 4
  const totalPages = Math.ceil(debates.length / debatesPerPage) || 1
  const paginatedDebates = debates.slice(
    (currentPage - 1) * debatesPerPage,
    currentPage * debatesPerPage,
  )

  return (
    <div className="w-full">
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
        <div className="flex w-full space-x-4">
          <Select value={subjectFilter} onValueChange={setSubjectFilter}>
            <SelectTrigger className="w-full md:w-1/4 border rounded px-3 py-2">
              <SelectValue placeholder="All Subjects" />
            </SelectTrigger>
            <SelectContent className="w-full">
              <SelectItem value="all">All Subjects</SelectItem>
              <SelectItem value="Artificial Intelligence">Artificial Intelligence</SelectItem>
              <SelectItem value="Robotics">Robotics</SelectItem>
              <SelectItem value="Ethics">Ethics</SelectItem>
              <SelectItem value="Environmental Issue">Environmental Issues</SelectItem>
              <SelectItem value="Climate Change">Climate Change</SelectItem>
              <SelectItem value="Privacy and Surveillance">Privacy and Surveillance</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedFormat} onValueChange={setSelectedFormat}>
            <SelectTrigger className="w-full md:w-1/4 border rounded px-3 py-2">
              <SelectValue placeholder="All Formats" />
            </SelectTrigger>
            <SelectContent className="w-full">
              <SelectItem value="all">All Formats</SelectItem>
              <SelectItem value="Lincoln Douglas">Lincoln Douglas</SelectItem>
              <SelectItem value="Quick Round">Quick Round</SelectItem>
              <SelectItem value="Team Policy">Team Policy</SelectItem>
              <SelectItem value="Public Forum">Public Forum</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Input
          type="text"
          placeholder="Search debates..."
          className="border rounded px-3 py-2 w-full md:w-1/3"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div
        className={`w-full flex flex-col flex-grow items-center ${
          paginatedDebates?.length > 0 && !isFetching ? 'justify-start' : 'justify-center'
        }`}
      >
        {isFetching ? (
          <div className="w-full h-full flex flex-grow justify-center items-center">
            <Loader />
          </div>
        ) : paginatedDebates?.length > 0 ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="h-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 pt-4"
            >
              {paginatedDebates.map((debate) => (
                <DebateCard key={debate.hallId} debate={debate} />
              ))}
            </motion.div>
          </AnimatePresence>
        ) : (
          <p className="text-center font-bold text-3xl">No Debates Found</p>
        )}

        {debates.length > 4 && (
          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show More ({debates.length - 4} more)
                </>
              )}
            </Button>
          </div>
        )}

        {isExpanded && totalPages > 1 && (
          <div className="mt-6">
            <Paginator
              totalItems={debates?.length || 0}
              itemsPerPage={debatesPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              variant="dots"
            />
          </div>
        )}
      </div>
    </div>
  )
}
